import { DealsGroupFindDto, type DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import { FilialFindDto, type FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { CostCenterFindDto, type CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import { PlanningScenarioFindDto, type PlanningScenarioFindInterface } from '@zaoblako/shared/modules/finance/dtos/planning-scenario/planning-scenario-find.dto';
import { UnitFindDto, type UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { CompanyFindDto, type CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ApprovalSchemeFindDto, type ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import { BudgetRequestItemFindDto, type BudgetRequestItemFindInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/item/budget-request-item-find.dto';
import { BudgetRequestApproverFindDto, type BudgetRequestApproverFindInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-approver.find.dto';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { BudgetRequestTypeEnum } from '@zaoblako/shared/modules/task/enum/budget-request-type.enum';
import { BudgetRequestAnalyticsTypeEnum } from '@zaoblako/shared/modules/task/enum/budget-request-analytics-type.enum';
import { BudgetRequestStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/budget.request.status.type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';

import * as yup from 'yup';

/** Budget request response body - DTO signature */
export interface BudgetRequestFindInterface {
  /** Unique identifier. Cannot be changes. */
  id: number;

  // Tech columns
  /** Budget request creation date and time */
  created: Date;
  /** Budget request last update date and time */
  updated: Date;
  /** Budget request soft deletion date and time (`null` for not deleted schemes) */
  deleted?: Date;
  /** Budget request creator */
  createdBy?: UserFindInterface;

  // Data columns
  /** Budget request type */
  type: BudgetRequestTypeEnum;
  /** Budget request analytics type. Using only when {@link BudgetRequestFindInterface.type} equals {@link BudgetRequestTypeEnum.PLAN}*/
  analyticsType?: BudgetRequestAnalyticsTypeEnum;
  /** Budget request display name */
  name: string;
  /** Budget request status */
  status: BudgetRequestStatusTypeEnum;
  /** Budget request period (month and year) */
  period: Date;
  /** Month to transfrer expense balances */
  transferDate?: Date;
  /** Approval scheme to use */
  scheme?: ApprovalSchemeFindInterface;
  /** Company to plan budget */
  company?: CompanyFindInterface;
  /** Unit analytical feature */
  unit?: UnitFindInterface;
  /** Filial analytical feature */
  filial?: FilialFindInterface;
  /** Cost center */
  costCenter?: CostCenterFindInterface;
  /** Planning scenario */
  scenario: PlanningScenarioFindInterface;

  /** Deals group to plan budget */
  dealsGroup?: DealsGroupFindInterface;
  /** Budget amount values per directions */
  items: BudgetRequestItemFindInterface[];

  // Other relations

  /** Approvers - main budget request participants which must approve it */
  approvers?: BudgetRequestApproverFindInterface[];
  /** User auditors - other budget request participants */
  userAuditors?: UserFindInterface[];
  /** User groups auditors - other budget request participants */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Related messenger thread */
  thread?: ThreadFindInterface;

  // Computed properties (not editable)
  /** Budget request total income amount */
  incomeAmount: number;
  /** Budget request total expense amount */
  expenseAmount: number;
  /** Total files count */
  filesCount?: number;
}

/** Budget request response body - DTO schema */
export const BudgetRequestFindDto = SerdeDtoClass.create<BudgetRequestFindInterface>(TaskDtoKeysEnum.BUDGET_REQUEST_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Обновлён',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Обновлён',
    serializer: yup.date(),
  },
  createdBy: {
    sortable: true,
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  type: {
    sortable: true,
    label: 'Тип',
    serializer: yup
      .string()
      .oneOf(Object.values(BudgetRequestTypeEnum)),
  },
  analyticsType: {
    optional: true,
    label: 'Тип аналитики',
    serializer: yup
      .string()
      .oneOf(Object.values(BudgetRequestAnalyticsTypeEnum)),
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  status: {
    sortable: true,
    label: 'Статус',
    serializer: yup
      .string()
      .oneOf(Object.values(BudgetRequestStatusTypeEnum)),
  },
  period: {
    sortable: true,
    label: 'Период',
    serializer: yup.date(),
  },
  transferDate: {
    optional: true,
    label: 'Период',
    serializer: yup.date(),
  },
  scheme: {
    sortable: true,
    optional: true,
    label: 'Схема согласования',
    nested: ApprovalSchemeFindDto,
    serializer: (_, nested) => nested,
  },
  company: {
    sortable: true,
    optional: true,
    label: 'Компания',
    nested: CompanyFindDto,
    serializer: (_, nested) => nested,
  },
  unit: {
    sortable: true,
    optional: true,
    label: 'БЕ',
    nested: UnitFindDto,
    serializer: (_, nested) => nested,
  },
  filial: {
    sortable: true,
    optional: true,
    label: 'Филиал',
    nested: FilialFindDto,
    serializer: (_, nested) => nested,
  },
  costCenter: {
    sortable: true,
    optional: true,
    label: 'Центр затрат',
    nested: CostCenterFindDto,
    serializer: (_, nested) => nested,
  },
  scenario: {
    sortable: true,
    label: 'Сценарий планирования',
    nested: PlanningScenarioFindDto,
    serializer: (_, nested) => nested,
  },
  dealsGroup: {
    sortable: true,
    optional: true,
    label: 'Проект',
    nested: DealsGroupFindDto,
    serializer: (_, nested) => nested,
  },
  items: {
    label: 'Бюджет по статьям',
    nested: BudgetRequestItemFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  approvers: {
    optional: true,
    label: 'Согласователи',
    nested: BudgetRequestApproverFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userAuditors: {
    optional: true,
    label: 'Пользователи-аудиторы',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userGroupAuditors: {
    optional: true,
    label: 'Группы-аудиторы',
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  thread: {
    optional: true,
    label: 'Тред в мессенджере',
    nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  incomeAmount: {
    sortable: true,
    label: 'Сумма доходов',
    serializer: yup.number(),
  },
  expenseAmount: {
    sortable: true,
    label: 'Сумма расходов',
    serializer: yup.number(),
  },
  filesCount: {
    optional: true,
    label: 'Прикреплено файлов',
    serializer: yup.number(),
  },
});
